import {
  collection,
  getDocs,
  doc,
  getDoc,
  query,
  where,
  limit as qlimit,
  startAfter,
  addDoc,
  orderBy,
  startAt,
  endAt,
  Timestamp,
  serverTimestamp,
  updateDoc,
  getCountFromServer,
  getAggregateFromServer,
  sum,
  count,
  setDoc,
  deleteDoc,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { ref as storageRef, uploadBytes, getDownloadURL } from "firebase/storage";
import { auth, db, functions, storage } from "./firebase";
import { isMissingIndexError } from "./firestoreErrors";
import { classifyTerm, mergeResults } from "../utils/peopleSearch";
import { isPendingVerification } from "../utils/verificationPresentation";
import { STAFF_ROLES } from "../config/permissions";
import { VERSION_DOC_ID } from "../utils/versionForm";

import { cyclePeriod } from "../utils/payCycle";
import { cycleBounds, cycleHours, emptyCycleDetails } from "../utils/teachingHours";
import { toDate } from "../utils/ticketPresentation";
import { startBoundOp } from "../utils/studentDetail";
import { withoutInternal } from "../utils/cyclePay";

/**
 * The pay-cycle rule history a super admin controls (spec 037), from config/app.
 * `null` on any failure, which `payCycle.js` reads as the shipped default — so a
 * config that will not load falls back to today's 26th->25th behaviour rather than
 * to no cycle at all.
 */
export const getPayCycleVersions = async () => {
  try {
    const snap = await getDoc(doc(db, "config", "app"));
    return snap.exists() ? snap.data()?.payCycle?.versions ?? null : null;
  } catch (e) {
    console.warn("payCycle config unreadable, using the default cycle:", e.message);
    return null;
  }
};

/**
 * The current and previous pay periods, straight off the versioned rule. Both edges
 * come from the chain because periods vary in length across a rule change — the old
 * hand-rolled "26th of this month to the 25th of next" could not express a bridge
 * period and would have re-sliced history the moment the rule moved.
 */
export const setPayCycle = async (startDay) => {
  // superAdmin-gated and validated on the server; the callable appends to the
  // history rather than overwriting it, so a change never re-slices a period that
  // already ran. Nothing here may write config/app directly — firestore.rules
  // refuses `payCycle` from every client, deliberately.
  const call = httpsCallable(functions, "setPayCycle");
  const { data } = await call({ startDay });
  return data;
};

export const getCyclesFor = (versions, now = new Date()) => ({
  current: cyclePeriod(versions, now),
  previous: cyclePeriod(versions, now, -1),
});

// Update user by ID.
//
// Profile fields only. Everything in firestore.rules' serverOnlyFields() — role,
// the balances, salary, the rating aggregates, disabled, referral — is refused
// here for admins too since spec 026 D21; each has its own callable
// (setStaffRole, adjustWallet, setTeacherSalary, moderateRating,
// setAccountDisabled). A payload carrying one of those fields fails the whole
// write, including the profile fields beside it.
export const updateUser = async (userId, data) => {
  const ref = doc(db, "users", userId);
  await updateDoc(ref, data);
  return true;
};

// Hours per tutor in the current and previous pay cycle, and the previous cycle's details, from
// every booking. The Tutors list keeps this whole-collection read until its own step (047 D37);
// the pay rule itself lives in utils/teachingHours (047 D47). A failed read logs and returns
// empty maps, as it always has.
export const getTutorsTeachingHoursCycle = async () => {
  try {
    const bounds = cycleBounds(getCyclesFor(await getPayCycleVersions(), new Date()));
    const snap = await getDocs(collection(db, "bookings"));
    return cycleHours(snap.docs.map((d) => ({ id: d.id, ...d.data() })), bounds);
  } catch (e) {
    console.error("Error calculating teaching hours:", e);
    return { current: {}, previous: {}, previousCycleDetails: {}, previousLessons: {} };
  }
};

// One tutor's hours in the current and previous pay cycle (047 D47): only their lessons that end
// inside the two cycles, on the (teacherId, endTimeUTC) index, counted by the list's rule. Every
// booking stores endTimeUTC as a Timestamp (staging, 2026-09-16: no string-typed ones), so the
// range misses nothing. Rethrown: "0 hours" and a failed read must not look the same.
export const getTutorCycleHours = async (teacherId, now = new Date()) => {
  const cycles = getCyclesFor(await getPayCycleVersions(), now);
  const snap = await getDocs(
    query(
      collection(db, "bookings"),
      where("teacherId", "==", teacherId),
      where("endTimeUTC", ">=", Timestamp.fromDate(cycles.previous.start)),
      where("endTimeUTC", "<", Timestamp.fromDate(cycles.current.end))
    )
  );
  const hours = cycleHours(snap.docs.map((d) => ({ id: d.id, ...d.data() })), cycleBounds(cycles));
  return {
    cycles,
    current: hours.current[teacherId] || 0,
    previous: hours.previous[teacherId] || 0,
    previousLessons: hours.previousLessons[teacherId] || [],
    details: hours.previousCycleDetails[teacherId] || emptyCycleDetails(),
  };
};

// Get All Teachers
export const getAllTeachers = async () => {
  try {
    const usersSnapshot = await getDocs(collection(db, "users"));
    const teachers = usersSnapshot.docs
      .map((doc) => ({ id: doc.id, ...doc.data() }))
      .filter((user) => user.role === "teacher");

    return teachers;
  } catch (error) {
    console.error("Error fetching teachers:", error);
    return [];
  }
};

// Get User by ID
export const getUserById = async (userId) => {
  try {
    const userDoc = await getDoc(doc(db, "users", userId));
    if (userDoc.exists()) {
      return { id: userDoc.id, ...userDoc.data() };
    }
    return null;
  } catch (error) {
    console.error("Error fetching user:", error);
    return null;
  }
};

// One ticket / one booking by id, for a deep link (spec 044 D2: a queue row
// lands on the thing itself). Same contract as getUserById: null when missing
// or unreadable, never a throw — the page then says "no longer exists".
export const getTicketById = async (ticketId) => {
  try {
    const snap = await getDoc(doc(db, "tickets", ticketId));
    return snap.exists() ? { id: snap.id, ...snap.data() } : null;
  } catch (error) {
    console.error("Error fetching ticket:", error);
    return null;
  }
};

export const getBookingById = async (bookingId) => {
  try {
    const snap = await getDoc(doc(db, "bookings", bookingId));
    return snap.exists() ? { id: snap.id, ...snap.data() } : null;
  } catch (error) {
    console.error("Error fetching booking:", error);
    return null;
  }
};

// Activity Log Functions
// Log an activity to Firestore
export const logActivity = async (activityData) => {
  try {
    const activityLog = {
      ...activityData,
      timestamp: Timestamp.now(),
      createdAt: Timestamp.now(),
    };
    const docRef = await addDoc(collection(db, "activityLogs"), activityLog);
    return docRef.id;
  } catch (error) {
    console.error("Error logging activity:", error);
    throw error;
  }
};

// Get all activity logs (for admins)
export const getActivityLogs = async (limit = 100) => {
  try {
    const col = collection(db, "activityLogs");
    const q = query(col, orderBy("timestamp", "desc"), qlimit(limit));
    const snap = await getDocs(q);
    const logs = snap.docs.map((d) => ({
      id: d.id,
      ...d.data(),
    }));
    return logs;
  } catch (error) {
    console.error("Error fetching activity logs:", error);
    return [];
  }
};

// Get activity logs by user ID
export const getActivityLogsByUser = async (userId, limit = 50) => {
  try {
    if (!userId) return [];
    const col = collection(db, "activityLogs");
    const q = query(
      col,
      where("userId", "==", userId),
      orderBy("timestamp", "desc"),
      qlimit(limit)
    );
    const snap = await getDocs(q);
    const logs = snap.docs.map((d) => ({
      id: d.id,
      ...d.data(),
    }));
    return logs;
  } catch (error) {
    console.error("Error fetching user activity logs:", error);
    return [];
  }
};

// ── Subjects (spec 024 T4) — the chip list the app's Search renders ──

// Live tutor count for one subject name. Shared by getSubjects (page-load
// snapshot for every row) and Subjects.js's write-time re-check (spec 024
// review R1: rules don't guard this — see D6 — so the client re-check right
// before a rename/delete IS the guard; the page-load count can go stale).
export const getSubjectTutorCount = async (name) => {
  const agg = await getCountFromServer(
    query(collection(db, "users"), where("subjects", "array-contains", name))
  );
  return agg.data().count;
};

export const getSubjects = async () => {
  const snap = await getDocs(
    query(collection(db, "subjects"), orderBy("order", "asc"))
  );
  const subjects = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  // tutorCount powers D6's rename/delete guard; a handful of subjects, so N
  // parallel count() aggregations, not a users scan.
  await Promise.all(
    subjects.map(async (s) => {
      s.tutorCount = await getSubjectTutorCount(s.name);
    })
  );
  return subjects;
};

// The Students list's Subject filter (057 D14): names only, in `order`; getSubjects also counts
// tutors per subject, which the filter does not need.
export const getSubjectNames = async () => {
  const snap = await getDocs(query(collection(db, "subjects"), orderBy("order", "asc")));
  return snap.docs.map((d) => d.data().name).filter(Boolean);
};

// The highest client.appVersion among students (057 D18), for the Latest / Older app filters.
// Needs the users index role ASC + client.appVersion DESC. Null when no student has a client.
export const getLatestAppVersion = async () => {
  const snap = await getDocs(
    query(collection(db, "users"), where("role", "==", "student"), orderBy("client.appVersion", "desc"), qlimit(1))
  );
  return snap.docs[0]?.data().client?.appVersion || null;
};

export const addSubject = async ({ id, name, order, icon }) =>
  setDoc(doc(db, "subjects", id), { name, order, ...(icon ? { icon } : {}) });

export const updateSubject = async (id, data) =>
  updateDoc(doc(db, "subjects", id), data);

export const deleteSubject = async (id) => deleteDoc(doc(db, "subjects", id));

// ── Plans (spec 024 T5) — the Wallet's top-up packs ──
export const getPlans = async () => {
  const snap = await getDocs(collection(db, "plans"));
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .sort((a, b) => (a.hours ?? 0) - (b.hours ?? 0));
};
export const addPlan = async (data) => addDoc(collection(db, "plans"), data);
export const updatePlan = async (id, data) => updateDoc(doc(db, "plans", id), data);
export const deletePlan = async (id) => deleteDoc(doc(db, "plans", id));

// ── Inbox (spec 024 T7, extended spec 026 S1-S3) — the queue reportLesson.js,
// lessonLifecycle.js and ticketBacklog.js raise into, and staff work from. The list's
// page and counts (047 D56) sit with the other cursor reads: getTicketsPage, countTickets ──

// A ticket's thread, oldest first — the first row is reportLesson.js's own
// free-text reason on a user report; a system ticket starts with none at all.
export const getTicketMessages = async (ticketId) => {
  const snap = await getDocs(
    query(collection(db, "tickets", ticketId, "messages"), orderBy("createdAt", "asc"))
  );
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
};

// The only way a reply lands — a plain Firestore write, same as the app's own
// composer (firestore.rules' messages block: staff create needs no callable).
// MESSAGE_MAX (src/utils/ticketContract.js) is enforced by the caller before
// this is ever invoked; the rule enforces the same 2000-char cap server-side.
// attachmentPath is optional (storage.rules' tickets/{id}/{fileId} write,
// widened to isStaff() alongside this composer — S3) — the messages rule has
// no hasOnly, so this extra field is already allowed.
export const sendTicketMessage = async (ticketId, text, attachmentPath) => {
  const payload = { senderId: auth.currentUser?.uid, text: text.trim(), createdAt: serverTimestamp() };
  if (attachmentPath) payload.attachmentPath = attachmentPath;
  return addDoc(collection(db, "tickets", ticketId, "messages"), payload);
};

// Mirrors storage.rules' tickets/{ticketId}/{fileId} cap exactly (image/*,
// under 5MB) — enforced there too, this is only the fast client-side check.
export const MAX_ATTACHMENT_BYTES = 5 * 1024 * 1024;

// Uploads one reply's image attachment to Storage and returns its path (not
// a download URL — read is still rule-gated per viewer, so the URL is
// resolved lazily by whoever renders the message; see getAttachmentUrl).
// fileId is a random id, matching the rule's {fileId} — any name works, one
// per file is all the rule cares about.
export const uploadTicketAttachment = async (ticketId, file) => {
  const path = `tickets/${ticketId}/${crypto.randomUUID()}`;
  await uploadBytes(storageRef(storage, path), file);
  return path;
};

export const getAttachmentUrl = (path) => getDownloadURL(storageRef(storage, path));

// The only way a ticket is resolved (functions/resolveTicket.js's
// runResolveTicket, spec 026 S2) — refuses an already-resolved or closed
// ticket, and needs `raisedBy` on the ticket to attach an optional credit.
// Wrapped exactly like recordPayout/reviewVerification/adjustWallet below:
// errors are rethrown, not swallowed — "resolved" must never be shown for a
// call that did not commit.
export const resolveTicket = async ({ ticketId, resolutionText, creditSessions }) => {
  const call = httpsCallable(functions, "resolveTicket");
  const { data } = await call({ ticketId, resolutionText, creditSessions });
  return data;
};

// The only way staff moves a ticket open ↔ waiting_user or resolved → closed
// (functions/resolveTicket.js's runSetTicketStatus). "resolved" is never a
// valid target here — resolveTicket above is the only door into it. Same
// wrapping as resolveTicket: rethrown, never swallowed.
export const setTicketStatus = async ({ ticketId, status }) => {
  const call = httpsCallable(functions, "setTicketStatus");
  const { data } = await call({ ticketId, status });
  return data;
};

// ── Attention queue (spec 024 D7) — count() aggregations, never scans ──
export const getAttentionCounts = async () => {
  const usersCol = collection(db, "users");
  const [tickets, pending, owed, internals] = await Promise.all([
    getCountFromServer(query(collection(db, "tickets"), where("status", "==", "open"))),
    // Legacy tutors with no verifyStatus field are aliased to "pending" for
    // display (Tutors.js), but not counted here: this tile counts real
    // applications — docs that actually carry verifyStatus: "pending" — so
    // this number and the Tutors page's "Pending" row count can differ.
    getCountFromServer(
      query(usersCol, where("role", "==", "teacher"), where("verifyStatus", "==", "pending"))
    ),
    getAggregateFromServer(query(usersCol, where("unpaidEarning", ">", 0)), {
      owedCount: count(),
      owedTotal: sum("unpaidEarning"),
    }),
    // 053 D11: internal (test) accounts leave "Teachers owed" — read apart, subtracted.
    getDocs(query(usersCol, where("isInternal", "==", true))),
  ]);
  const real = withoutInternal(
    { count: owed.data().owedCount, total: owed.data().owedTotal },
    internals.docs.map((d) => d.data())
  );
  return {
    openTickets: tickets.data().count,
    pendingTeachers: pending.data().count,
    owedCount: real.count,
    owedTotal: real.total,
  };
};

// ── Ctrl+K people search (spec 024 D4) — people only ──
export const PHONE_ALIASES = ["phone", "phone_number", "mobile", "whatsapp", "contact"];
// Firestore's prefix-range sentinel: the highest Private Use Area codepoint,
// written as an escape so no editor or copy step can eat the raw character.
const PREFIX_END = "\uf8ff";

// `role` keeps the search to students or teachers (the Students page, a notification's chosen
// people): the name query is scoped by the users (role, name) index, so the other role's hits
// cannot fill its five; email has no such index, so it reads further and is filtered below.
// ponytail: a (role, email) index if a short email prefix ever hides a role past 25 hits.
export const searchPeople = async (term, { role } = {}) => {
  const { kind, values } = classifyTerm(term);
  const usersCol = collection(db, "users");
  const byRole = role ? [where("role", "==", role)] : [];
  const queries =
    kind === "phone"
      ? PHONE_ALIASES.flatMap((f) =>
          values.map((v) => query(usersCol, where(f, "==", v), qlimit(5)))
        )
      : values.flatMap((v) => [
          // PREFIX_END turns an orderBy range into a prefix match;
          // endAt(v) alone would match only exact hits.
          query(usersCol, ...byRole, orderBy("name"), startAt(v), endAt(v + PREFIX_END), qlimit(5)),
          query(usersCol, orderBy("email"), startAt(v), endAt(v + PREFIX_END), qlimit(role ? 25 : 5)),
        ]);
  const snaps = await Promise.all(queries.map((q) => getDocs(q)));
  return mergeResults(
    snaps.map((s) => s.docs.map((d) => ({ id: d.id, ...d.data() })))
  ).filter((u) => (role ? u.role === role : u.role === "student" || u.role === "teacher"));
};

// ── Verification queue (spec 026 V2) — approve/reject a teacher's onboarding ──

// Teachers awaiting review. Own full-collection read rather than reusing
// getAllTeachers (the page is admin-only and low-traffic): getAllTeachers
// swallows a denied read as [], and an empty queue and a denied read must not
// look the same on a review page. Filters with the
// same rule the attention tile counts by (verificationPresentation.js): docs
// that actually carry verifyStatus/verify_status "pending", not legacy docs
// missing the field.
export const getPendingTeachers = async () => {
  try {
    const snap = await getDocs(collection(db, "users"));
    return snap.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .filter((u) => u.role === "teacher" && isPendingVerification(u));
  } catch (error) {
    console.error("Error fetching pending teachers:", error);
    throw error;
  }
};

// The only way a verification decision is ever written (functions/reviewVerification.js,
// spec 026 V1) — approves or rejects, stamps verifyReviewedAt/By, and notifies the teacher.
// Wrapped exactly like recordPayout below: errors are rethrown, not swallowed — "reviewed"
// must never be shown for a call that did not commit. An approval carries the tutor's
// hourly rate (026 D22) — the server refuses "approved" without a number above zero and
// writes it with the status, so the rate is asked for in the Approve dialog, not later.
export const reviewVerification = async ({ teacherId, decision, reason, salary }) => {
  const call = httpsCallable(functions, "reviewVerification");
  const { data } = await call({ teacherId, decision, reason, salary });
  return data;
};

// Every rating a tutor has received — ratings/{lessonId}_{studentUid}, written only
// by the rateLesson callable (spec 025), so each row carries lessonId, ratedBy,
// rating, comment and createdAt. Filtered on `ratedTo` alone, which Firestore's
// automatic single-field index serves; newest first is sorted here rather than
// with an orderBy, so the read needs no composite index the cutover would have to
// carry. Rethrown, not swallowed: an empty list and a denied read must not look
// the same on a moderation screen.
export const getTutorRatings = async (teacherId) => {
  const snap = await getDocs(
    query(collection(db, "ratings"), where("ratedTo", "==", teacherId)),
  );
  const at = (row) => toDate(row.createdAt)?.getTime() ?? 0;
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .sort((a, b) => at(b) - at(a));
};

// A tutor's latest ratings, newest first, on the (ratedTo, createdAt desc) index (047 D103): the
// lesson page's Previous ratings. A rating without createdAt is left out, as the index leaves it.
export const getLatestTutorRatings = async (teacherId, n = 4) => {
  const snap = await getDocs(
    query(collection(db, "ratings"), where("ratedTo", "==", teacherId), orderBy("createdAt", "desc"), qlimit(n))
  );
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
};

// The only way a rating row is ever removed (functions/moderateRating.js, spec 026
// D5/D24): the row's deletion and the tutor's rating/numberOfRatings recompute are
// one transaction, so the public average can never outlive the row behind it.
// Wrapped exactly like reviewVerification above — rethrown, never swallowed — and
// the returned {rating, numberOfRatings} is authoritative: the drawer shows it
// instead of re-deriving its own.
export const moderateRating = async ({ ratingId }) => {
  const call = httpsCallable(functions, "moderateRating");
  const { data } = await call({ ratingId });
  return data;
};

// ── Wallet adjustment (phase-2 Slice W, task W1) ──

// The only way an admin adjusts a wallet balance outside the normal
// lesson/top-up flow (functions/adjustWallet.js). Signed delta in sessions;
// the server writes the new users/{userId}.WalletBalance and a
// walletTransactions ledger row (transactionType: "adminAdjustment").
// Wrapped exactly like reviewVerification/recordPayout above: errors are
// rethrown, not swallowed — "adjusted" must never be shown for a call that
// did not commit.
export const adjustWallet = async ({ userId, delta, reason }) => {
  const call = httpsCallable(functions, "adjustWallet");
  const { data } = await call({ userId, delta, reason });
  return data;
};

// ── Ban / unban an account (phase-2 task P2) ──

// The only way an account is ever locked out of signing in
// (functions/setAccountDisabled.js): Auth first, then users/{userId}.disabled, reverted
// together on failure so the two never disagree. Wrapped exactly like adjustWallet
// above: errors are rethrown, not swallowed — "disabled" must never be shown for a call
// that did not commit.
export const setAccountDisabled = async ({ userId, disabled }) => {
  const call = httpsCallable(functions, "setAccountDisabled");
  const { data } = await call({ userId, disabled });
  return data;
};

// ── The staff doors on a lesson (spec 044 D9) ──

// functions/staffCancelBooking.js: cancel with an explicit refund-or-not and a reason;
// the server moves the credit (or the free session) and tells both parties. Wrapped
// like adjustWallet above: errors are rethrown — "cancelled" must never be shown for
// a call that did not commit.
export const staffCancelBooking = async ({ lessonId, refund, reason }) => {
  const call = httpsCallable(functions, "staffCancelBooking");
  const { data } = await call({ lessonId, refund, reason });
  return data;
};

// functions/finishLesson.js `staffFinishLesson`: the tutor's Finish outcome (status,
// pay, both notifications) with the staff member recorded as `finishedBy`.
export const staffFinishLesson = async ({ lessonId }) => {
  const call = httpsCallable(functions, "staffFinishLesson");
  const { data } = await call({ lessonId });
  return data;
};

// functions/createTicket.js `staffCreateTicket`: a ticket raised on a person's behalf,
// optionally about a lesson. Returns { ticketId, number }.
export const staffCreateTicket = async ({ userId, category, text, bookingId = null }) => {
  const call = httpsCallable(functions, "staffCreateTicket");
  const { data } = await call({ userId, category, text, bookingId });
  return data;
};

// functions/staffSendNotification.js (spec 044 D32): one free-text notification to
// one person — an in-app item plus a push. Admin and up, or the notifications grant
// (047 D64); errors are rethrown like the other staff doors above, so "sent" is never
// shown for a call that did not commit.
export const staffSendNotification = async ({ userId, title, body }) => {
  const call = httpsCallable(functions, "staffSendNotification");
  const { data } = await call({ userId, title, body });
  return data;
};

// users/{parentId}/children/{childId}.name for the Learner line (spec 044 D13).
// `pairs` is [{ parentId, childId }]; the result is keyed "parentId/childId". A
// child that no longer exists — or a denied read — is simply absent, and the label
// falls back to "Child". Rules: `children` read is staff-wide since 044 slice 3.
export const getChildNames = async (pairs) => {
  const unique = new Map(pairs.map((p) => [`${p.parentId}/${p.childId}`, p]));
  const names = {};
  await Promise.all(
    [...unique].map(async ([key, { parentId, childId }]) => {
      try {
        const snap = await getDoc(doc(db, "users", parentId, "children", childId));
        if (snap.exists() && snap.data().name) names[key] = snap.data().name;
      } catch (error) {
        console.error("Error fetching child:", error);
      }
    })
  );
  return names;
};

// Leads captured by the website's ads landing page (121islam.com/landing-page).
// Written unauthenticated by the marketing site into `leads`; read here by admins.
// Rethrow rather than returning []: "no leads yet" and "your read was denied" are
// very different answers for a sales page, and must not look the same.
export const getLeads = async (limit = 200) => {
  try {
    const q = query(collection(db, "leads"), orderBy("createdAt", "desc"), qlimit(limit));
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch (error) {
    console.error("Error fetching leads:", error);
    throw error;
  }
};

// ── Staff management + version editor (phase-2 task P3, Settings page) ──

// Everyone whose users/{uid}.role is one of the four staff roles — a where-in
// query, since the staff set is the fixed STAFF_ROLES list permissions.js and the
// rules' isStaff() both name. Rethrow rather than returning []: "no staff" and
// "your read was denied" must not look the same on the page that hands out admin
// power.
//
// "user" rides along because demoting is otherwise one-way: setStaffRole's fifth
// value drops someone to "user", and this is the only screen that could put them
// back. It does not drag the app's userbase in — app accounts carry
// "student"/"teacher" (provisionUser.js), never "user". Five values, still under
// where-in's ten.
export const getStaffUsers = async () => {
  try {
    const q = query(collection(db, "users"), where("role", "in", [...STAFF_ROLES, "user"]));
    const snap = await getDocs(q);
    return snap.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .sort((a, b) => (a.name || a.id).localeCompare(b.name || b.id));
  } catch (error) {
    console.error("Error fetching staff users:", error);
    throw error;
  }
};

// The only way users/{uid}.role ever changes after signup (functions/setStaffRole.js,
// spec 026 P3): superAdmin, or an admin for moderators and accountants (044 D4);
// refuses demoting the last superAdmin. Wrapped exactly like setAccountDisabled
// above: errors are rethrown, not swallowed — a role change must never be shown for
// a call that did not commit.
// Spec 044 D5: `grants` is optional and forwarded only when the caller names it —
// an undefined key would still be a key, and the function reads "grants present"
// as "replace them".
export const setStaffRole = async ({ userId, role, grants }) => {
  const call = httpsCallable(functions, "setStaffRole");
  const { data } = await call({ userId, role, ...(grants !== undefined ? { grants } : {}) });
  return data;
};

// How the dashboard adds a staff account (functions/createStaffAccount.js, spec 044
// D11/D62): Auth account + users doc + the ADD_STAFF activity row in one server call.
// Wrapped exactly like setStaffRole above: errors are rethrown, not swallowed.
export const createStaffAccount = async ({ name, email, password, role }) => {
  const call = httpsCallable(functions, "createStaffAccount");
  const { data } = await call({ name, email, password, role });
  return data;
};

// The only way users/{uid}.salary and .fixedMonthlyPay ever change
// (functions/setTeacherSalary.js, spec 026 D21; fixedMonthlyPay added 053 D8):
// admin-only, teachers only. The tutor editor wrote salary straight through
// `updateUser` until D21 — that write was the reason serverOnlyFields() could
// not guard the rules' isAdmin() branch. Wrapped exactly like setStaffRole above:
// errors are rethrown, not swallowed — a new rate must never be shown for a call that
// did not commit. Returns `previousSalary`, which is what the caller's audit row
// records as `before`. `fixedMonthlyPay` is optional: omitted, the server leaves it
// alone.
export const setTeacherSalary = async ({ userId, salary, fixedMonthlyPay }) => {
  const call = httpsCallable(functions, "setTeacherSalary");
  const { data } = await call({ userId, salary, ...(fixedMonthlyPay === undefined ? {} : { fixedMonthlyPay }) });
  return data;
};

// The force-update gate's doc — the app reads version/<id> on launch and foreground
// and blocks itself when iosVersion/androidVersion outrank the running build.
// Rethrow: an unreadable gate and "no gate configured" must not look the same here.
export const getVersionDoc = async () => {
  try {
    const snap = await getDoc(doc(db, "version", VERSION_DOC_ID));
    return snap.exists() ? snap.data() : {};
  } catch (error) {
    console.error("Error fetching version doc:", error);
    throw error;
  }
};

// Direct doc write, like updateSubject/updatePlan above — staging rules already
// allow `write: if isAdmin()` on /version/{docId}; no callable needed. Merged
// rather than updateDoc: getVersionDoc tolerates the doc being absent (returns
// {} and the form still enables), and updateDoc would throw on that same state.
export const updateVersionDoc = async (payload) =>
  setDoc(doc(db, "version", VERSION_DOC_ID), payload, { merge: true });

// --- Lesson Management: a bounded window instead of the whole collection ---
//
// This page used to make three whole-collection reads
// on every load: the entire bookings collection, plus the entire users
// collection twice (once filtered to students, once to teachers). Everything —
// tabs, search, sorting — then ran client-side over that array, and the table
// rendered every matching row. It got slower every day the platform ran.
//
// Admins browse recent lessons, so the fix is to read a window of the most
// recent ones and let "Load more" reach further back. The page keeps its
// client-side search, filters and sort, which now operate over the loaded
// window rather than the whole history.

// Ordering is on startTimeUTC. Both writers of a booking (createBooking.js,
// checkoutCart.js) refuse to create one without it, and — the reason it is
// startTimeUTC rather than endTimeUTC, which differs only by the lesson's 25 or
// 55 minutes — production already carries a `(status ASC, startTimeUTC ASC)`
// composite index, and Firestore serves an equality-filtered query in either
// direction from it. So the per-tab query below needs no new index.
//
// The Firestore semantic this leans on twice: a document MISSING the orderBy
// field is excluded, not sorted last. That is a caveat for startTimeUTC (a
// legacy booking without one would drop off the list) and the whole mechanism
// for the Reported tab, where the sparse `reportedAt` index is the filter.
export const LESSON_WINDOW_SIZE = 500;

// One window of lessons for one tab.
//
// The status filter runs on the SERVER, which is what makes the tabs correct
// however many bookings exist: "Cancelled" reads the 500 most recent cancelled
// lessons, not the cancelled ones that happen to fall inside a global window of
// 500. Search and the remaining dropdowns stay client-side over whatever is
// loaded — Firestore cannot do substring search, and "Load more" is how you
// reach further back.
export const getRecentBookings = async ({
  status = "",
  orderField = "startTimeUTC",
  pageSize = LESSON_WINDOW_SIZE,
  cursor = null,
} = {}) => {
  const run = (withStatus) => {
    const parts = [collection(db, "bookings")];
    if (withStatus && status) parts.push(where("status", "==", status));
    parts.push(orderBy(orderField, "desc"));
    if (cursor) parts.push(startAfter(cursor));
    parts.push(qlimit(pageSize));
    return getDocs(query(...parts));
  };

  let snap;
  // True when the server did the filtering, so the window holds only this
  // tab's lessons. False after the fallback below, where the caller's own
  // predicate has to do it over a window of everything.
  let serverFiltered = Boolean(status);
  try {
    snap = await run(true);
  } catch (error) {
    // The status query needs a (status ASC, startTimeUTC DESC) composite index.
    // Until it exists — or while it is still building — fall back to the
    // unfiltered window rather than showing the admin an error: that is exactly
    // the behaviour this screen had before, so it degrades, it does not break.
    if (status && isMissingIndexError(error)) {
      console.warn(
        "bookings(status, startTimeUTC desc) index missing — filtering the recent window on the client instead:",
        error?.message
      );
      serverFiltered = false;
      snap = await run(false);
    } else {
      console.error("Error fetching recent bookings:", error);
      // Surfaced rather than swallowed: an empty table and a failed read must
      // not look the same on a screen whose tabs are the navigation.
      throw error;
    }
  }

  return {
    rows: snap.docs.map((d) => ({ id: d.id, ...d.data() })),
    // The raw snapshot, not a value: startAfter(doc) is stable across documents
    // that share a startTimeUTC, which a value cursor is not.
    cursor: snap.docs.length ? snap.docs[snap.docs.length - 1] : null,
    // A short page means the query is exhausted. A full one only means "maybe
    // more", which is the honest thing to tell the button.
    hasMore: snap.docs.length === pageSize,
    serverFiltered,
  };
};

// The Lessons list's head (047 D94): every lesson, the completed ones and the cancelled ones, each
// one count() on `bookings` (the automatic indexes serve). Each fails alone and reads null, so its
// tile shows "—" and the others still count.
export const getLessonCounts = async () => {
  const one = (...clauses) =>
    getCountFromServer(query(collection(db, "bookings"), ...clauses))
      .then((snap) => snap.data().count)
      .catch((error) => {
        console.error("Error counting lessons:", error);
        return null;
      });
  const [total, completed, cancelled] = await Promise.all([
    one(),
    one(where("status", "==", "Finished")),
    one(where("status", "==", "Cancelled")),
  ]);
  return { total, completed, cancelled };
};

// The list's RATING column (047 D96): the ratings of one page of lessons, 30 ids per `in` (the
// query's cap). Throws: the column then stays empty rather than saying "Not rated".
export const getRatingsForLessons = async (ids = []) => {
  const unique = [...new Set(ids.filter(Boolean))];
  const chunks = [];
  for (let i = 0; i < unique.length; i += 30) chunks.push(unique.slice(i, i + 30));
  const snaps = await Promise.all(chunks.map((c) => getDocs(query(collection(db, "ratings"), where("lessonId", "in", c)))));
  return snaps.flatMap((snap) => snap.docs.map((d) => ({ id: d.id, ...d.data() })));
};

// What the lesson page reads beside the lesson (047 D101–D103): its wallet rows (createBooking's
// charge names it `bookingId`, a cancel's rows `lessonId`), its tickets and its ratings. Equality
// only, so the automatic indexes serve. Throws: the tabs say what failed.
export const getLessonRecords = async (lessonId) => {
  const read = (name, field) => getDocs(query(collection(db, name), where(field, "==", lessonId)));
  const rows = (snap) => snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  const [charged, cancelled, tickets, ratings] = await Promise.all([
    read("walletTransactions", "bookingId"),
    read("walletTransactions", "lessonId"),
    read("tickets", "bookingId"),
    read("ratings", "lessonId"),
  ]);
  return { walletRows: [...rows(charged), ...rows(cancelled)], tickets: rows(tickets), ratings: rows(ratings) };
};

// One read of `users`, split locally. This page used to scan the whole
// collection once for students and again for teachers, paying for every user
// document twice.
export const getStudentsAndTeachers = async () => {
  try {
    const snap = await getDocs(collection(db, "users"));
    const all = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    return {
      students: all.filter((u) => u.role === "student"),
      teachers: all.filter((u) => u.role === "teacher"),
    };
  } catch (error) {
    console.error("Error fetching students and teachers:", error);
    return { students: [], teachers: [] };
  }
};

// ── Students list (spec 044 slice 4a, D19/D21) ──
//
// Never the whole collection. The page reads one cursor window for the
// (filter, sort) pair studentPresentation.js turned into `spec`; the count is an
// aggregation over the same filter. Both throw rather than return []: on a
// list whose count is the headline, "none" and "the read failed" must not look
// alike — the page shows an Alert with Retry, as Lessons does.

const studentsQuery = ({ where: clauses, orderBy: order }, { cursor = null, pageSize = 0 } = {}) => {
  const parts = [collection(db, "users"), ...clauses.map(([field, op, value]) => where(field, op, value))];
  if (order) parts.push(orderBy(order[0], order[1]));
  if (cursor) parts.push(startAfter(cursor));
  if (pageSize) parts.push(qlimit(pageSize));
  return query(...parts);
};

export const getStudentsPage = async (spec, { pageSize = 25, cursor = null } = {}) => {
  // A loud failure beats a silent scan of the whole collection (D19).
  if (!(pageSize > 0)) throw new Error(`getStudentsPage: pageSize must be positive, got ${pageSize}`);
  const snap = await getDocs(studentsQuery(spec, { cursor, pageSize }));
  return {
    rows: snap.docs.map((d) => ({ id: d.id, ...d.data() })),
    // The raw snapshot, as getRecentBookings keeps it: startAfter(doc) breaks
    // ties on the document id, which a value cursor cannot.
    cursor: snap.docs.length ? snap.docs[snap.docs.length - 1] : null,
    hasMore: snap.docs.length === pageSize,
  };
};

// The count carries the filter AND the order: an aggregation over the very
// query the page runs is served by the same composite index, and it counts
// only documents that carry the order field — so `total` never exceeds what
// the pages can reach (a document missing `name` or `lessonSummary.lastAt`
// is invisible to both).
export const countStudents = async (spec) => {
  const snap = await getCountFromServer(studentsQuery(spec));
  return snap.data().count;
};

// A student's children, names only, for the Learners cell. Staff may read the
// subcollection (044 D13); the names never ride on the user document (002 D5).
// Capped at 20 — the cell shows names, the count comes from childCount.
export const getStudentChildren = async (uid) => {
  const snap = await getDocs(query(collection(db, "users", uid, "children"), qlimit(20)));
  return snap.docs.map((d) => ({ id: d.id, name: d.data().name || "" }));
};

// ── The student page (spec 044 D29–D36) ──

// A student's children with the fields the Children tab and the child page show
// (002: name, date_of_birth, gender, createdAt). Capped at 20 like getStudentChildren;
// unordered here — an orderBy would drop a legacy child with no createdAt, so the
// caller sorts (studentDetail.sortChildren).
export const getStudentChildrenFull = async (uid) => {
  const snap = await getDocs(query(collection(db, "users", uid, "children"), qlimit(20)));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
};

export const getChild = async (uid, childId) => {
  const snap = await getDoc(doc(db, "users", uid, "children", childId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
};

// The startTimeUTC bound for a `when` (D38): none without one; with one it needs a status
// (D38 splits Pending, and only that shape was checked on staging), and an unknown `when`
// throws rather than silently widening the query.
const startBound = (when, now, status) => {
  if (!when) return [];
  const op = startBoundOp(when);
  if (!op) throw new Error(`startBound: when must be "future" or "past", got ${when}`);
  if (!status) throw new Error("startBound: when needs a status (044 D38)");
  return [where("startTimeUTC", op, Timestamp.fromDate(now))];
};

// One page of a person's bookings, newest first (D35). `field` is studentId, or teacherId for
// the tutor page (047 step 4), whose indexes mirror the student's. `status` rides the
// (field, status, startTimeUTC desc) index, `childId` the student's
// (studentId, learner.childId, startTimeUTC desc) one; both together would need a
// fourth index nobody asked for, so that shape is refused rather than left to fail
// on staging. `when` ("future" | "past") bounds startTimeUTC at `now` on the status
// index (D38), so it needs a status.
const bookingsQuery = (field, uid, { status = null, childId = null, when = null, now = new Date(), cursor = null, pageSize } = {}) => {
  if (status && childId) {
    throw new Error("bookingsQuery: status and childId together are not indexed (044 D35)");
  }
  const parts = [collection(db, "bookings"), where(field, "==", uid)];
  if (status) parts.push(where("status", "==", status));
  if (childId) parts.push(where("learner.childId", "==", childId));
  parts.push(...startBound(when, now, status));
  parts.push(orderBy("startTimeUTC", "desc"));
  if (cursor) parts.push(startAfter(cursor));
  parts.push(qlimit(pageSize));
  return query(...parts);
};

// One page from a query asked for pageSize + 1 rows: the extra row only answers "is
// there a next page", so an exactly full last page offers no Next into nothing.
const pageOf = (docs, pageSize) => {
  const page = docs.slice(0, pageSize);
  return {
    rows: page.map((d) => ({ id: d.id, ...d.data() })),
    cursor: page.length ? page[page.length - 1] : null,
    hasMore: docs.length > pageSize,
  };
};

export const getStudentBookingsPage = async (
  uid,
  { status = null, childId = null, when = null, now = new Date(), pageSize = 25, cursor = null } = {}
) => {
  // A loud failure beats a silent scan of the whole collection (D19).
  if (!(pageSize > 0)) throw new Error(`getStudentBookingsPage: pageSize must be positive, got ${pageSize}`);
  const snap = await getDocs(bookingsQuery("studentId", uid, { status, childId, when, now, cursor, pageSize: pageSize + 1 }));
  return pageOf(snap.docs, pageSize);
};

// The tutor page's Sessions tab (047 step 4): the student's page read on teacherId, without the
// child filter (no tutor index carries it).
export const getTutorBookingsPage = async (uid, { status = null, when = null, now = new Date(), pageSize = 25, cursor = null } = {}) => {
  if (!(pageSize > 0)) throw new Error(`getTutorBookingsPage: pageSize must be positive, got ${pageSize}`);
  const snap = await getDocs(bookingsQuery("teacherId", uid, { status, when, now, cursor, pageSize: pageSize + 1 }));
  return pageOf(snap.docs, pageSize);
};

// Without a `when`, a count has equality filters only — no order, so no composite index
// (D35): Firestore merges the single-field indexes. `status` and `childId` may both be set
// here. A `when` (D38) adds the startTimeUTC bound and the page's order, so the count rides
// the same (studentId, status, startTimeUTC desc) index as the page (REST-checked on staging).
export const countStudentBookings = async (uid, { status = null, childId = null, when = null, now = new Date() } = {}) => {
  if (when && childId) throw new Error("countStudentBookings: when and childId together are not indexed (044 D38)");
  const parts = [collection(db, "bookings"), where("studentId", "==", uid)];
  if (status) parts.push(where("status", "==", status));
  if (childId) parts.push(where("learner.childId", "==", childId));
  const bound = startBound(when, now, status);
  if (bound.length) parts.push(...bound, orderBy("startTimeUTC", "desc"));
  const snap = await getCountFromServer(query(...parts));
  return snap.data().count;
};

// A tutor's lesson counts (047 D48), on countStudentBookings' shapes: equality only needs no
// composite index; a `when` rides (teacherId, status, startTimeUTC desc).
export const countTutorBookings = async (uid, { status = null, when = null, now = new Date() } = {}) => {
  const parts = [collection(db, "bookings"), where("teacherId", "==", uid)];
  if (status) parts.push(where("status", "==", status));
  const bound = startBound(when, now, status);
  if (bound.length) parts.push(...bound, orderBy("startTimeUTC", "desc"));
  const snap = await getCountFromServer(query(...parts));
  return snap.data().count;
};

// A tutor's completed lessons that carry a note to the parent (047 D49; the note is
// bookings.parentNote, 029 D14), on (teacherId, status, parentNote.createdAt). `!= null` goes
// out as IS_NOT_NULL, the shape that index was checked with.
export const countTutorNotes = async (uid) => {
  const snap = await getCountFromServer(
    query(
      collection(db, "bookings"),
      where("teacherId", "==", uid),
      where("status", "==", "Finished"),
      where("parentNote.createdAt", "!=", null)
    )
  );
  return snap.data().count;
};

// Every payout recorded for one tutor (047 D52): equality only, so no composite index, and
// sorted by the page (tutorDetail.sortPayouts). A payout is a once-a-cycle event, so the list
// stays short. Rules: payouts read is staff-wide.
export const getTutorPayouts = async (teacherId) => {
  const snap = await getDocs(query(collection(db, "payouts"), where("teacherId", "==", teacherId)));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
};

// One page of a person's wallet ledger, newest first, on the (userId, createdAt desc)
// index (D35). Replaces the old drawer's unordered 20-row read — an unordered limit
// returns an arbitrary 20 once the ledger is long.
export const getWalletTransactionsPage = async (uid, { pageSize = 25, cursor = null } = {}) => {
  if (!(pageSize > 0)) throw new Error(`getWalletTransactionsPage: pageSize must be positive, got ${pageSize}`);
  const parts = [collection(db, "walletTransactions"), where("userId", "==", uid), orderBy("createdAt", "desc")];
  if (cursor) parts.push(startAfter(cursor));
  parts.push(qlimit(pageSize + 1));
  const snap = await getDocs(query(...parts));
  return pageOf(snap.docs, pageSize);
};

// The tickets about one person: the ones they raised (raisedBy) and the ones about
// their lessons (`aboutField`: studentId, or teacherId on the tutor page — reportLesson.js,
// lessonLifecycle.js and createTicket.js's staff tickets about a lesson write both ids;
// the system tickets have raisedBy null). Both order on `raisedAt`, which every writer
// sets: `lastActivityAt` exists only where createTicket.js or a message put it, so
// ordering on it would drop a system ticket nobody has answered (D35, amended). The
// caller merges them (studentDetail.mergeTickets). No paging: the newest 50 of each.
const getPersonTickets = async (uid, aboutField, limit) => {
  const rows = (snap) => snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  const [raised, about] = await Promise.all([
    getDocs(query(collection(db, "tickets"), where("raisedBy", "==", uid), orderBy("raisedAt", "desc"), qlimit(limit))),
    getDocs(query(collection(db, "tickets"), where(aboutField, "==", uid), orderBy("raisedAt", "desc"), qlimit(limit))),
  ]);
  return { raised: rows(raised), about: rows(about) };
};

export const getStudentTickets = (uid, { limit = 50 } = {}) => getPersonTickets(uid, "studentId", limit);

// On (teacherId, raisedAt desc), added for the tutor page (047 step 4).
export const getTutorTickets = (uid, { limit = 50 } = {}) => getPersonTickets(uid, "teacherId", limit);

// The Inbox list (047 D56): one status, newest first, on the (status, raisedAt desc) index. Every
// ticket writer sets raisedAt; lastActivityAt is missing where no message was written. The count
// asks the same query without a page, so it counts exactly the tickets the pages reach. Both
// throw: the list says what failed.
const ticketsQuery = (status, { cursor = null, pageSize = null } = {}) => {
  const parts = [collection(db, "tickets"), where("status", "==", status), orderBy("raisedAt", "desc")];
  if (cursor) parts.push(startAfter(cursor));
  if (pageSize) parts.push(qlimit(pageSize));
  return query(...parts);
};

export const getTicketsPage = async (status, { pageSize = 25, cursor = null } = {}) => {
  if (!(pageSize > 0)) throw new Error(`getTicketsPage: pageSize must be positive, got ${pageSize}`);
  const snap = await getDocs(ticketsQuery(status, { cursor, pageSize: pageSize + 1 }));
  return pageOf(snap.docs, pageSize);
};

export const countTickets = async (status) => {
  const snap = await getCountFromServer(ticketsQuery(status));
  return snap.data().count;
};

// The users documents behind a set of ids (the teacher names on a sessions page).
// One read per distinct id; the caller caches across pages (tabs/useUsersByIds.js).
export const getUsersByIds = async (ids = []) => {
  const unique = [...new Set(ids.filter(Boolean))];
  const users = await Promise.all(unique.map((id) => getUserById(id)));
  return Object.fromEntries(users.filter(Boolean).map((u) => [u.id, u]));
};
